import React, { Component, Fragment } from "react"
import { connect } from "react-redux"
import PropTypes from "prop-types"
import classNames from "classnames"

/* Selectors */
import selectAuthentication from "selectors/authentication"
import selectProducts from "selectors/products"
import { selectQuery } from "selectors/location"
import { selectReferencesModel } from "selectors/references"
import selectorNavigator from "selectors/navigator"

/* Helpers */
import TrackingSegment from "managers/segmentManager"
import { updateMeta, restoreDefaultMeta } from "helpers/meta"
import { getAssetURL } from "helpers/static"

/* Components */
import FAQSection from "components/PageMarketingLanding/FAQSection/FAQSection"
import Icon, { IconTypes } from "components/Icon/Icon"

/* Consts */
import consts from "consts"
import styles from "./pageLandingStrapi.css"
import { getStrapiData } from "actions/strapi"

const illuDeviceStrapi = getAssetURL("image.pageTravel.illuDeviceStrapi")
const stars = getAssetURL("image.pageTravel.stars")
const starsx2 = getAssetURL("image.pageTravel.starsx2")

@connect((state, ownProps) => {
  const navigator = selectorNavigator(state)

  return {
    appSettings: state.appSettings,
    isAuthenticated: selectAuthentication(state).isAuthenticated,
    products: selectProducts(state),
    references: selectReferencesModel(state),
    query: selectQuery(ownProps),
    windowWidth: state.ui.windowWidth,
    location: ownProps.location,
    strapi: state.strapi.attributes,
    UA: navigator.isIos ? "IOS" : navigator.isAndroid ? "Android" : "Desktop",
  }
})
export default class PageLandingStrapi extends Component {
  static propTypes = {
    appSettings: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    products: PropTypes.object,
    references: PropTypes.object,
    query: PropTypes.object.isRequired,
    isAuthenticated: PropTypes.bool,
    windowWidth: PropTypes.number,
    UA: PropTypes.string,
    strapi: PropTypes.object,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }

  strapiLandingChannels = [
    "tf1",
    "france-2",
    "france-3",
    "france-5",
    "m6",
    "arte",
    "w9",
    "tmc",
    "nt-1",
    "lcp-public-senat",
    "france-4",
    "bfm-tv",
    "itele",
    "cstar",
    "gulli",
    "hd1",
    "l-equipe",
    "6ter",
    "numero-23",
    "rmc-decouverte",
    "lci",
  ]

  static fetchData(dispatch) {
    return [dispatch(getStrapiData())]
  }

  static getHeadContent() {
    return (
      <Fragment>
        <link rel="alternate" href={consts.externalLink.landing} hrefLang="fr-fr" />
        <link rel="alternate" href={consts.externalLink.landing} hrefLang="x-default" />
      </Fragment>
    )
  }

  getMeta() {
    return this.props.strapi.meta
  }

  getCTA = cta => {
    cta = !cta?.url || !cta?.text ? this.props.strapi.header_cta : cta

    return (
      <div className={styles.subWrapper}>
        <a className={styles.subscribe} href={cta.url} target={cta.open_new_window ? "_blank" : ""}>
          {cta.text}
        </a>
      </div>
    )
  }

  componentDidMount() {
    TrackingSegment.trackPage(this.props.strapi.track_page_name)
    updateMeta(this.getMeta())
  }

  componentWillUnmount() {
    restoreDefaultMeta()
  }

  render() {
    const { references, strapi } = this.props
    const channelsList = strapi.channels_list

    this.strapiLandingChannels =
      channelsList.length > 0 ? channelsList.map(channel => channel.name.replace("six-ter", "6ter")) : this.strapiLandingChannels

    const channelsReferences = references
      .getTvBundles()
      .reduce((acc, bundle) => {
        return acc.concat(bundle.channels)
      }, [])
      .filter(channel => !channel.isVirtual())

    const channels = this.strapiLandingChannels.map(channel => channelsReferences.find(ch => ch.data.slug_seo === channel))
    const customBackgroundHeader = {}

    if (strapi.background_media?.data?.attributes?.url && strapi.background_media.data.attributes.mime.includes("image")) {
      customBackgroundHeader["background-image"] = "url('" + strapi.background_media?.data?.attributes?.url + "')"
    } else if (strapi.background_media?.data?.attributes?.url && strapi.background_media.data.attributes.mime.includes("video")) {
      customBackgroundHeader["background-image"] = "none"
    }

    return (
      <div className={styles.root}>
        <div className={styles.hero} style={customBackgroundHeader}>
          <div className={classNames(styles.heroContentHead)}>
            <Icon type={IconTypes.logoWithFubo} iconStyle={styles.logoStyle} />
            <h1>{strapi.main_title}</h1>
            <p>{strapi.subtitle}</p>
            {this.getCTA()}
          </div>
          <div className={styles.backgroundVideoContainer}>
            <video className={styles.backgroundVideo} preload autoPlay muted="true" loop playsinline="true" volume="0" id="background_video">
              <source src={strapi.background_media?.data?.attributes?.url} type="video/mp4" />
            </video>
          </div>
        </div>
        <div className={styles.mtvPlus}>
          <div className={styles.mtvPlusContent}>
            <div className={styles.mainOfferImg}>
              <img loading="lazy" src={strapi.main_offer_image?.data?.attributes?.url || illuDeviceStrapi} />
            </div>
            <div className={styles.mainOfferSection}>
              <h2>{strapi.main_offer_title}</h2>
              <div className={styles.mainOfferContent} dangerouslySetInnerHTML={{ __html: strapi.main_offer_content }} />
              {this.getCTA(strapi.main_offer_cta)}
            </div>
          </div>
        </div>
        <div className={classNames([styles.hero, styles.heroChannels])}>
          <div className={styles.heroContent}>
            <h2>{strapi.channels_title}</h2>
          </div>
          <div className={styles.channelList}>
            {channels.map((channel, index) => (
              <div className={styles.channel} key={`${channel?.getId() || 0}-${index}`}>
                <img loading="lazy" src={channel?.getImageUrl("medium", "dark").replace("http:", "https:")} />
              </div>
            ))}
            <div className={styles.channel}>
              <img loading="lazy" src="https://images.molotov.tv/data/landing/_1655217351_More%20channels.png" />
            </div>
          </div>
          <div>{this.getCTA(strapi.channels_cta)}</div>
        </div>
        <div className={styles.awards}>
          <p>
            <span>{strapi.total_user}</span>
            <br />
            d'utilisateurs
          </p>
          <img loading="lazy" className={styles.stars} src={stars} srcSet={`${stars} 1x, ${starsx2} 2x`} />
        </div>
        <div className={classNames([styles.devices, styles.programs])}>
          <h2 className={styles.programsTitle}>{strapi.programs_title}</h2>
          <div className={classNames([styles.channelFramework, styles.programFramework])}>
            {strapi.programs_images?.data &&
              Object.keys(strapi.programs_images.data).map(key => <img src={strapi.programs_images.data[key].attributes.url} />)}
          </div>
          {this.getCTA(strapi.programs_cta)}
        </div>
        <div className={styles.devices}>
          <h2 className={styles.devicesTitle}>{strapi.devices_title}</h2>
          <div className={styles.channelFramework}>
            {strapi.devices_images?.data &&
              Object.keys(strapi.devices_images.data).map(key => <img src={strapi.devices_images.data[key].attributes.url} />)}
          </div>
          {this.getCTA(strapi.devices_cta)}
        </div>
        <div className={styles.faq}>
          <h2>{strapi.qa_title}</h2>
          <FAQSection sections={strapi.q_a} />
        </div>
      </div>
    )
  }
}
